export const getCommaSeparatedValues = (arr: any[]) => {
    if (!arr || arr.length === 0) {
        return;
    }
    const str = arr.filter(val => val !== null && val !== "").join(", ");
    return str;
};

export const getQueryParameterFromUri = (uri: string | undefined, parameterName: string) => {
    let queryParameter = "";
    if (uri) {
        const queryParams = uri.split("?")[1];
        const urlParams = new URLSearchParams(queryParams);
        queryParameter = urlParams.get(parameterName) || "";
    }
    return queryParameter;
};

export const formatPhone = (value: string): string => {
    // Receives phone number as 9988776655 or a partial one like 99439
    // Output - 998-877-6655
    if (!value) {
        return "";
    }
    value = value.split("-").join("");
    const length = value.length;
    if (length <= 3) {
        return value;
    }
    let modifiedValue = "";
    if (length >= 4 && length <= 6) {
        modifiedValue = `${value.substring(0, 3)}-${value.substring(3)}`;
    } else if (length > 6) {
        modifiedValue = `${value.substring(0, 3)}-${value.substring(3, 6)}-${value.substring(6)}`;
    }
    return modifiedValue;
};

export function convertDateToDateString(date: Date | undefined) {
    // return date in "dd-MMM-yyyy"
    if (!date) {
        return "";
    }
    const month = date.toLocaleString("default", { month: "short" });
    const day = date.getDate();
    const dateString = day < 10 ? `0${day}` : day;

    return `${dateString}-${month}-${date.getFullYear()}`;
}

export const generateQueryString = (obj: { [key: string]: any }) => {
    // generates a query string from sn Object
    const data: { [key: string]: any } = {};
    for (const key in obj) {
        if (obj[key]) {
            data[key] = obj[key];
        }
    }
    const queryParams = new URLSearchParams(data);
    return queryParams.toString();
};

export const isExpiryDatePassed = (expiryDate: Date, currentDate: Date) => {
    // Set the time of both dates to midnight to ignore time
    expiryDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);
    // Compare the dates in miliseconds
    return expiryDate.getTime() < currentDate.getTime();
};

export const convertToDateFormat = (dateStr: string) => {
    // input: "04-Dec-2022"
    // output: "YYYY-MM-DDT00:00:00"
    // Parse the input date manually
    const parsedDate = new Date(Date.parse(dateStr));

    // Format to "YYYY-MM-DDT00:00:00"
    const formattedDate = `${parsedDate.toISOString().split("T")[0]}T00:00:00`;
    return formattedDate;
};

export function convertDateStringToFormatmddyyyy(dateString: string | undefined): Date | undefined {
    // input: "20-Feb-2025"
    // output: "2/20/2025"
    if (!dateString) {
        return undefined;
    }

    return new Date(dateString); // Let JS handle it in local time
}

export const parseCommaSeparated = (value?: string | null): string[] =>
    value
        ?.split(",")
        .map(v => v.trim())
        .filter(Boolean) || [];

// new site restriction utility functions

export interface UserRestrictionParams {
    userName?: string | null;
    currentUserIsGuest?: boolean;
    isPunchOutSession?: boolean;
    userRoles?: string;
    customerNumber?: string | number | null;
    isBillToLoading?: boolean;
    restrictedRoles?: string[];
    restrictedCustomers?: string[];
}

export interface RestrictionResult {
    shouldStayOnLegacy: boolean;
    shouldWaitForData: boolean;
}

/**
 * Determines if a user should stay on the legacy site based on various restrictions
 * This function handles ALL restriction logic including punchout, roles, customers, etc.
 */
export const checkSiteRestrictions = (params: UserRestrictionParams): RestrictionResult => {
    // Guest users - allowed to go to new site
    if (params.currentUserIsGuest) {
        return { shouldStayOnLegacy: true, shouldWaitForData: false };
    }
    // Guest users - allowed to go to new site
    if (params.userName === null || params.userName === undefined || params.userName === "") {
        return { shouldStayOnLegacy: true, shouldWaitForData: true };
    }

    // Punchout users - must stay on legacy
    if (params.isPunchOutSession) {
        return { shouldStayOnLegacy: true, shouldWaitForData: false };
    }

    // If billTo is still loading, we need to wait
    if (params.isBillToLoading) {
        return { shouldStayOnLegacy: true, shouldWaitForData: true };
    }

    // Check if we have customer data when needed
    const hasCustomerData =
        params.customerNumber !== undefined &&
        params.customerNumber !== null &&
        String(params.customerNumber).trim() !== "";

    const shouldCheckCustomerRestriction = !!params.restrictedCustomers?.length;

    if (shouldCheckCustomerRestriction && !hasCustomerData) {
        return { shouldStayOnLegacy: true, shouldWaitForData: true };
    }

    // Check role restrictions
    const userRolesArray = parseCommaSeparated(params.userRoles || "");

    if (params.restrictedRoles && userRolesArray.some(role => params.restrictedRoles!.includes(role))) {
        return { shouldStayOnLegacy: true, shouldWaitForData: false };
    }

    // Check customer restriction with debug logs
    if (hasCustomerData && params.restrictedCustomers) {
        const customerNumberStr = String(params.customerNumber);

        const matchResult = params.restrictedCustomers.some(rc => {
            const rcStr = String(rc);
            const isMatch = rcStr === customerNumberStr;

            return isMatch;
        });

        if (matchResult) {
            return { shouldStayOnLegacy: true, shouldWaitForData: false };
        }
    }

    // No restrictions - allowed to go to new site
    return { shouldStayOnLegacy: false, shouldWaitForData: false };
};

export const shouldStayOnLegacy = (params: UserRestrictionParams): boolean => {
    return checkSiteRestrictions(params).shouldStayOnLegacy;
};

export const shouldRedirectToNewSite = (params: UserRestrictionParams): boolean => {
    const result = checkSiteRestrictions(params);
    // Only redirect to new site if NOT staying on legacy AND NOT waiting for data
    return !result.shouldStayOnLegacy && !result.shouldWaitForData;
};

export const appendLegacyParam = (url: string): string => {
    if (!url) {
        return url;
    }
    if (url.includes("legacy=true")) {
        return url;
    }
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}legacy=true`;
};

export const formatDate = (
    dateString: string,
    format: "MM/DD/YYYY" | "MM-DD-YYYY" | "DD/MM/YYYY" = "MM/DD/YYYY",
): string => {
    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
        return dateString;
    }

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    switch (format) {
        case "MM-DD-YYYY":
            return `${month}-${day}-${year}`;
        case "DD/MM/YYYY":
            return `${day}/${month}/${year}`;
        default:
            return `${month}/${day}/${year}`;
    }
};

export const extractCategoriesFromPath = (productDetailPath: string): string[] => {
    if (!productDetailPath) {
        return [];
    }
    const pathSegments = productDetailPath.split("/").filter((segment: string) => segment.length > 0);
    const categorySegments = pathSegments.slice(1, -1);
    const categories = categorySegments.map((segment: string) => segment.replace(/-en$/, "").replace(/-/g, " "));
    return categories;
};

export const getCategoryListInfo = (categories: string[]): { listName: string; listId: string } => {
    const listName = categories[categories.length - 1] || "";
    const listId = listName.toLowerCase().replace(/\s+/g, "_");
    return { listName, listId };
};

export interface PriceItem {
    price: number;
    quantity: number;
}

export function getTotalPrice(items: PriceItem[]): number {
    const value = items.reduce((total, item) => total + (item.price * item.quantity), 0);
    return Number(value.toFixed(2));
}
